import React from 'react';

// Styles & Assets
import styles from './EditUserDialog.module.css';

// Utils
import { getFullName } from '../../../services/getPatientAtribute';

// Hooks
import { useState } from 'react';
import useFetchDataEditUser from './useFetchDataEditUser';

// Components
import ButtonEditForm from '../ButtonEditForm/ButtonEditForm';
import Input from '../../UI/Input/Input';
import InputSelect from '../../UI/InputSelect/InputSelect';
import CheckboxButton from '../../UI/CheckboxButton/CheckboxButton';
import MultiSelector from '../../UI/MultiSelector/MultiSelector';

// Interfaces
import { UserData } from '../../../interfaces/UserInfo';
import { OptionsSelect } from '../../../interfaces/Input';

// Language
import { useTranslation } from 'react-i18next';
import useFormEditUser from './useFormEditUser';

const EditUserDialog: React.FC<{
    user: UserData,
    updateUser: (user: UserData) => void,
    setShowEditUserDialog: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ user, updateUser, setShowEditUserDialog }) => {

    const { t } = useTranslation();

    const defaultRoleIDs = user.user_roles?.map(r => r.role_id);
    const [activeUser, setActiveUser] = useState<boolean>(user.active);
    const [email, setEmail] = useState<string>(user.email);
    const [roleIDs, setRoleIDs] = useState<number[]>(defaultRoleIDs);
    const [isReferenceDoctor, setIsReferenceDoctor] = useState<boolean>(user.is_reference_doctor);
    const [referenceDoctorId, setReferenceDoctorId] = useState<number | null>(user.reference_doctor_id);

    const { roles, referenceDoctors } = useFetchDataEditUser();

    const callback = (user: UserData) => {
        setShowEditUserDialog(false);
        updateUser(user);
    }

    const { onConfirm } = useFormEditUser(user, roles, callback);

    const handleChangeEmail = (evt: React.FormEvent) => setEmail((evt.target as HTMLInputElement).value);

    const handleChangeRole = (id: number | string) => {
        const newID = Number(id);
        setRoleIDs(oldIDs => oldIDs.includes(newID) ? oldIDs.filter(i => i !== newID) : [...oldIDs, newID]);
    }

    const handleClickCheckbox = () => setIsReferenceDoctor(oldIsReferenceDoc => !oldIsReferenceDoc);

    const handleChangeReferenceDoc = (option: OptionsSelect) => {
        const referenceDoctorID = referenceDoctors.find(doc => doc.id === option.id)?.id;
        setReferenceDoctorId(referenceDoctorID ?? null);
    };

    const userHasRole = (roleID: number) => roleIDs?.some(id => id === roleID);

    const optionsRole = roles.map(r => ({ id: r.id, name: r.name ?? '', checked: userHasRole(r.id) }));

    const referenceDoctor = referenceDoctors.find(doc => doc.id === referenceDoctorId);

    const hasMedicRole = roleIDs?.some(id => roles.find(r => r.id === id)?.code === 'medic');

    const referenceDoctorsOption = referenceDoctors.map(doc => {
        return {
            id: doc.id,
            name: doc.first_name + ' ' + doc.last_name_1,
        };
    });


    const handleConfirm = () => {
        onConfirm(roleIDs, activeUser, email, isReferenceDoctor, referenceDoctorId);
    }

    return (
        <div className='dialog'>
            <div className={styles.userFormContainer}>
                <h1 className='dialogTitle'>{t('edit_user')}</h1>
                <h2 className='dialogSubTitle'>{user.first_name + ' ' + user.last_name_1}</h2>
                <div className={styles.formEditUser}>
                    <ButtonEditForm
                        label={t('active_user')}
                        active={activeUser}
                        setActive={setActiveUser}
                    />
                    {/* <ButtonEditForm
                        label={t('administration_user')}
                        active={isAdmin}
                        setActive={setIsAdmin}
                    /> */}
                    <Input
                        label={t('email')}
                        defaultValue={email}
                        onChange={handleChangeEmail}
                    />
                    <div className={styles.roleInput}>
                        <p className={styles.label}>{t('role')}</p>
                        <MultiSelector
                            options={optionsRole}
                            handleChange={handleChangeRole}
                        />
                        {hasMedicRole &&
                            <div className={styles.checkboxContainer}>
                                <CheckboxButton
                                    checked={isReferenceDoctor}
                                    onClick={handleClickCheckbox}
                                    label={t('reference_doctor')}
                                />
                            </div>
                        }
                        {(!hasMedicRole || (hasMedicRole && !isReferenceDoctor)) &&
                            <div className={styles.referenceDoctorContainer}>
                                <InputSelect
                                    options={referenceDoctorsOption}
                                    label={t('reference_doctor')}
                                    value={getFullName(referenceDoctor)}
                                    onChange={handleChangeReferenceDoc}
                                />
                            </div>
                        }
                    </div>

                </div>
            </div>
            <div className={styles.buttonsContainers}>
                <button onClick={() => setShowEditUserDialog(false)} className={`${styles.buttonCancel} button`}>{t('cancel')}</button>
                <button onClick={handleConfirm} className={`${styles.buttonConfirm} button`}>{t('confirm')}</button>
            </div>
        </div>
    );
};

export default EditUserDialog;